import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  cancelLootRedemption,
  getLootRedemptions,
  resumeLootRedemption,
  startLootRedemption,
} from '../api/lootRedemptionsApi';

const OPEN_STATUSES = ['reserved', 'claimed', 'payment_pending', 'manual_review'];
const STATUS_LABELS = {
  reserved: 'Reserviert',
  claimed: 'Im Shop geöffnet',
  payment_pending: 'Zahlung offen',
  redeemed: 'Eingelöst',
  released: 'Freigegeben',
  expired: 'Abgelaufen',
  manual_review: 'Manuelle Prüfung',
};

export default function LootRedemptionPanel() {
  const [state, setState] = useState(null);
  const [quantity, setQuantity] = useState(10);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const next = await getLootRedemptions();
      setState(next);
      const available = Number(next?.inventory?.available || 0);
      setQuantity((current) => Math.max(10, Math.min(Number(current || 10), Math.max(10, available))));
      setError('');
    } catch (loadError) {
      setError(loadError?.message || 'Einlösestatus konnte nicht geladen werden.');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const inventory = state?.inventory || {};
  const open = useMemo(
    () => (state?.redemptions || []).filter((entry) => OPEN_STATUSES.includes(entry.status)),
    [state]
  );
  const available = Number(inventory.available || 0);
  const canStart = Boolean(inventory.feature_available) && available >= 10 && quantity >= 10 && quantity <= available && !busy;

  const start = async () => {
    if (!canStart) return;
    setBusy('start');
    setError('');
    try {
      const result = await startLootRedemption(Number(quantity));
      window.location.assign(result.redirect_url);
    } catch (startError) {
      setError(startError?.message || 'Einlösung konnte nicht gestartet werden.');
      setBusy('');
      await load();
    }
  };

  const resume = async (id) => {
    setBusy(id);
    setError('');
    try {
      const result = await resumeLootRedemption(id);
      window.location.assign(result.redirect_url);
    } catch (resumeError) {
      setError(resumeError?.message || 'Einlösung kann nicht fortgesetzt werden.');
      setBusy('');
      await load();
    }
  };

  const cancel = async (id) => {
    if (!window.confirm('Diese noch nicht bestellte Einlösung wirklich abbrechen?')) return;
    setBusy(id);
    setError('');
    try {
      await cancelLootRedemption(id);
      setBusy('');
      await load();
    } catch (cancelError) {
      setError(cancelError?.message || 'Einlösung konnte nicht abgebrochen werden.');
      setBusy('');
    }
  };

  if (!state && !error) {
    return <div className="pfp-loot-redemption pfp-loot-redemption--loading">Einlösestatus wird geladen …</div>;
  }
  if (!state && error) {
    return <div className="pfp-loot-redemption pfp-loot-redemption--error">{error}</div>;
  }
  if (Number(inventory.total || 0) <= 0 && !open.length) {
    return null;
  }

  return (
    <section className="pfp-loot-redemption" aria-labelledby="pfp-loot-redemption-title">
      <div className="pfp-loot-redemption__header">
        <div>
          <span className="pfp-loot-redemption__eyebrow">Echte Aufkleber</span>
          <h3 id="pfp-loot-redemption-title">Pfandpirat-Aufkleber im Shop einlösen</h3>
        </div>
        <span className="pfp-loot-redemption__minimum">Ab 10 Stück</span>
      </div>

      <div className="pfp-loot-redemption__balances">
        <div><span>Gesamtbestand</span><strong>{Number(inventory.total || 0)}</strong></div>
        <div><span>Reserviert</span><strong>{Number(inventory.reserved || 0)}</strong></div>
        <div><span>Frei verfügbar</span><strong>{available}</strong></div>
      </div>

      <div className="pfp-loot-redemption__form">
        <label htmlFor="pfp-loot-redemption-quantity">Einlösemenge</label>
        <input
          id="pfp-loot-redemption-quantity"
          type="number"
          inputMode="numeric"
          min="10"
          max={Math.max(10, available)}
          step="1"
          value={quantity}
          onChange={(event) => setQuantity(Number(event.target.value))}
        />
        <button type="button" className="pfand-button" onClick={start} disabled={!canStart}>
          {busy === 'start' ? 'Wird reserviert …' : 'Im Shop einlösen'}
        </button>
      </div>

      {!inventory.feature_available && (
        <p className="pfp-loot-redemption__notice">Die Einlösung ist technisch vorbereitet und wird nach der Endabnahme freigeschaltet.</p>
      )}
      {available < 10 && (
        <p className="pfp-loot-redemption__notice">Du brauchst mindestens 10 frei verfügbare Aufkleber.</p>
      )}
      {error && <p className="pfp-loot-redemption__error" role="alert">{error}</p>}

      {open.length > 0 && (
        <div className="pfp-loot-redemption__open">
          <h4>Offene Einlösungen</h4>
          {open.map((entry) => (
            <div className="pfp-loot-redemption__entry" key={entry.redemption_id}>
              <div>
                <strong>{entry.quantity} Aufkleber</strong>
                <span>{STATUS_LABELS[entry.status] || entry.status}</span>
              </div>
              <div className="pfp-loot-redemption__actions">
                {!entry.has_order && ['reserved', 'claimed'].includes(entry.status) && (
                  <button type="button" onClick={() => resume(entry.redemption_id)} disabled={Boolean(busy)}>
                    Einlösung fortsetzen
                  </button>
                )}
                {!entry.has_order && ['reserved', 'claimed'].includes(entry.status) && (
                  <button type="button" className="is-danger" onClick={() => cancel(entry.redemption_id)} disabled={Boolean(busy)}>
                    Abbrechen
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

